import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, Loader2, X, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExcelImport = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        setError('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        setError('Por favor selecciona un archivo Excel (.xlsx o .xls)');
        return;
      }
      setFile(droppedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/import/excel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        if (onImportComplete) onImportComplete();
      } else {
        setError(data.detail || 'Error al importar el archivo');
      }
    } catch (err) {
      setError('Error de conexión. Inténtalo de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    // Create a sample Excel template content
    const templateData = `Cine,Dirección,GoogleMaps,Logo,Website,Lat,Lon,Fechas,Horarios,Formato,URLCompra
Cines Lys,"Pg. de Russafa, 3, 46002 Valencia",https://maps.google.com/?q=cines+lys,,,39.4699,-0.3763,06/03/2026-15/03/2026,"17:00, 19:30, 22:00",Doblada,https://entradas.cineslys.com
Abc Park,"C/ Roger de Llória, 21, Valencia",https://maps.google.com/?q=abc+park,,,39.4702,-0.3701,"06/03/2026, 08/03/2026, 10/03/2026","18:00, 20:30",VOSE,https://abcpark.es/entradas
Kinepolis Valencia,"C/ Comercial Ademuz",https://maps.google.com/?q=kinepolis,,,39.5012,-0.4589,10/03/2026,SOLO_ENLACE,,https://kinepolis.es/entradas`;

    const blob = new Blob([templateData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_cines_sesiones.csv';
    link.click();
  };

  return (
    <Card className="border-dashed border-2 border-gray-300 hover:border-[#50c878] transition-colors">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#50c878]" />
            Importar desde Excel
          </div>
          <Dialog open={showHelp} onOpenChange={setShowHelp}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#50c878]">
                <Info className="w-4 h-4 mr-1" />
                Ayuda
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Formato del Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Columnas del Excel:</h4>
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left">Columna</th>
                        <th className="border p-2 text-left">Descripción</th>
                        <th className="border p-2 text-left">Ejemplo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td className="border p-2 font-medium">Cine *</td><td className="border p-2">Nombre del cine</td><td className="border p-2">Cines Lys</td></tr>
                      <tr><td className="border p-2">Dirección</td><td className="border p-2">Dirección completa</td><td className="border p-2">Pg. Russafa, 3</td></tr>
                      <tr><td className="border p-2">GoogleMaps</td><td className="border p-2">URL de Google Maps</td><td className="border p-2">https://maps...</td></tr>
                      <tr><td className="border p-2">Logo</td><td className="border p-2">URL del logo</td><td className="border p-2">https://...</td></tr>
                      <tr><td className="border p-2">Website</td><td className="border p-2">Sitio web</td><td className="border p-2">https://...</td></tr>
                      <tr><td className="border p-2">Lat</td><td className="border p-2">Latitud</td><td className="border p-2">39.4699</td></tr>
                      <tr><td className="border p-2">Lon</td><td className="border p-2">Longitud</td><td className="border p-2">-0.3763</td></tr>
                      <tr><td className="border p-2">Fechas</td><td className="border p-2">Fecha(s) de las sesiones</td><td className="border p-2">Ver formatos abajo</td></tr>
                      <tr><td className="border p-2">Horarios</td><td className="border p-2">Horarios de las sesiones</td><td className="border p-2">17:00, 19:30</td></tr>
                      <tr><td className="border p-2">Formato</td><td className="border p-2">Doblada o VOSE</td><td className="border p-2">Doblada</td></tr>
                      <tr><td className="border p-2">URLCompra</td><td className="border p-2">URL para comprar</td><td className="border p-2">https://...</td></tr>
                    </tbody>
                  </table>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Formatos de Fechas:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li><strong>Fecha única:</strong> 06/03/2026</li>
                    <li><strong>Rango de fechas:</strong> 06/03/2026-15/03/2026</li>
                    <li><strong>Días sueltos:</strong> 06/03/2026, 08/03/2026, 10/03/2026</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2">Modo Solo Enlace (sin horarios):</h4>
                  <p className="text-gray-600">
                    Para cines que solo tienen botón "Comprar entradas" sin horarios específicos, 
                    deja la columna <strong>Horarios</strong> vacía o escribe <strong>SOLO_ENLACE</strong>
                  </p>
                </div>
                
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-blue-800 text-xs">
                    <strong>Nota:</strong> Si un cine ya existe, se actualizará con los nuevos datos. 
                    Las sesiones existentes se mantendrán y se añadirán las nuevas.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drop Zone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            file ? 'border-[#50c878] bg-[#50c878]/5' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-[#50c878]" />
              <div className="text-left">
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <Button variant="ghost" size="sm" onClick={resetUpload} className="ml-2">
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">
                Arrastra tu archivo Excel aquí o
              </p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-[#50c878] text-[#50c878] hover:bg-[#50c878] hover:text-white"
              >
                Seleccionar archivo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 bg-[#50c878] hover:bg-[#40a0a0]"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </>
            )}
          </Button>
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Plantilla
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Success Result */}
        {result && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Importación completada</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center p-2 bg-white rounded">
                <p className="text-2xl font-bold text-[#50c878]">{result.cinemas_created}</p>
                <p className="text-gray-500">Cines creados</p>
              </div>
              <div className="text-center p-2 bg-white rounded">
                <p className="text-2xl font-bold text-[#50c878]">{result.cinemas_updated}</p>
                <p className="text-gray-500">Cines actualizados</p>
              </div>
              <div className="text-center p-2 bg-white rounded">
                <p className="text-2xl font-bold text-[#50c878]">{result.sessions_created}</p>
                <p className="text-gray-500">Sesiones creadas</p>
              </div>
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                <p className="font-medium text-yellow-700 mb-1">Advertencias:</p>
                {result.errors.map((err, i) => (
                  <p key={i} className="text-yellow-600">{err}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExcelImport;
